import { Fragment, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Download } from 'lucide-react';
import Layout from '../components/Layout';
import api from '../services/api';
import toast from 'react-hot-toast';
import { groupByReferenceMonth } from '../lib/referencePeriod';

const MAXPAR_LABELS = {
  sent: 'Enviado',
  pending: 'Pendente',
  failed: 'Falha'
};

/**
 * Reads the download name sent by the API.
 */
function fileNameFromDisposition(header, fallback) {
  const match = /filename="([^"]+)"/.exec(header || '');
  return match ? match[1] : fallback;
}

/**
 * Reads an error message from a blob response.
 */
async function downloadErrorMessage(error, fallback) {
  const payload = error.response?.data;
  if (payload instanceof Blob) {
    try {
      return JSON.parse(await payload.text()).error || fallback;
    } catch (parseError) {
      return fallback;
    }
  }
  return error.response?.data?.error || fallback;
}

/**
 * Shows the processings the signed-in user may see, and the issue actions for an administrator.
 */
function ProductProcessings() {
  const [params, setParams] = useSearchParams();
  const productId = params.get('product') || '';
  const [products, setProducts] = useState([]);
  const [result, setResult] = useState(null);
  const [query, setQuery] = useState(params.get('q') || '');
  const [loading, setLoading] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState(null);

  useEffect(() => {
    let active = true;
    api.get('/product-processings/products')
      .then((response) => {
        if (active) setProducts(response.data.products || []);
      })
      .catch(() => {
        if (active) toast.error('Erro ao carregar os produtos');
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!productId) return undefined;
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const response = await api.get('/product-processings', {
          params: { product: productId, q: query }
        });
        if (active) setResult(response.data);
      } catch (error) {
        if (active) toast.error(error.response?.data?.error || 'Erro ao consultar os processamentos');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [productId, query]);

  const productLabel = products.find((product) => product.id === productId)?.label || result?.productLabel || '';
  const guaibim = productId === 'guaibim-resolve';
  const canIssue = Boolean(result?.canIssue);

  /**
   * Applies a file-name search from the first page of results.
   */
  const submitSearch = (event) => {
    event.preventDefault();
    const nextQuery = event.target.elements.q.value.trim();
    setQuery(nextQuery);
    const next = new URLSearchParams(params);
    if (nextQuery) next.set('q', nextQuery);
    else next.delete('q');
    setParams(next, { replace: true });
  };

  /**
   * Downloads the conference workbook or the invoice of one processing.
   */
  const downloadReport = async (item, kind) => {
    const isPdf = kind === 'pdf';
    const key = `${item.id}:${kind}`;
    try {
      setDownloadingKey(key);
      const response = await api.get(`/product-processings/${item.id}/${isPdf ? 'invoice' : 'excel'}`, {
        params: { product: productId },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], {
        type: isPdf
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileNameFromDisposition(
        response.headers['content-disposition'],
        isPdf ? 'fatura.pdf' : 'planilha.xlsx'
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(await downloadErrorMessage(error, isPdf ? 'Erro ao gerar a fatura' : 'Erro ao gerar o Excel'));
    } finally {
      setDownloadingKey(null);
    }
  };

  if (!productId || (products.length > 0 && !productLabel)) {
    return (
      <Layout headerTitle="Processamentos" headerSubtitle="Consulta por produto">
        <div className="p-6 max-w-3xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Processamentos</h1>
          <p className="text-gray-600 mb-6">Escolha o produto para ver os arquivos processados na tela e os que chegaram por e-mail.</p>
          <div className="grid gap-3">
            {products.map((product) => (
              <button
                key={product.id}
                type="button"
                onClick={() => setParams({ product: product.id })}
                className="text-left bg-white border border-gray-200 rounded-lg px-4 py-3 hover:border-primary-300"
              >
                {product.label}
              </button>
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  const items = result?.items || [];
  const columnCount = (guaibim ? 7 : 6) + (canIssue ? 1 : 0);
  const groups = groupByReferenceMonth(items, (item) => item.originalName);

  return (
    <Layout headerTitle={productLabel || 'Processamentos'} headerSubtitle="Consulta de processamentos">
      <div className="p-6 max-w-6xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{productLabel}</h1>
          <p className="text-gray-600">
            {canIssue
              ? 'Arquivos processados por qualquer usuário. Você pode emitir a planilha e a fatura de cada um.'
              : 'Arquivos que você enviou, na tela ou pela caixa de e-mail.'}
          </p>
        </div>

        <form onSubmit={submitSearch} className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col sm:flex-row gap-3">
          <label className="flex-1">
            <span className="block text-sm font-medium text-gray-700 mb-1">Nome do arquivo</span>
            <input name="q" defaultValue={query} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
          </label>
          <button type="submit" className="sm:self-end px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
            Buscar
          </button>
        </form>

        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-3">Quando</th>
                <th className="px-4 py-3">Arquivo</th>
                <th className="px-4 py-3">Origem</th>
                <th className="px-4 py-3">Quem enviou</th>
                <th className="px-4 py-3">Situação</th>
                <th className="px-4 py-3">Registros</th>
                {guaibim && <th className="px-4 py-3">Ativos</th>}
                {guaibim && <th className="px-4 py-3">Cancelados</th>}
                {!guaibim && <th className="px-4 py-3">MAXPAR</th>}
                {canIssue && <th className="px-4 py-3">Relatórios</th>}
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <Fragment key={group.key}>
                  <tr>
                    <td colSpan={columnCount} className="px-4 py-2 bg-gray-100 text-sm font-semibold text-gray-800">
                      {group.label}
                    </td>
                  </tr>
              {group.items.map((item) => (
                <tr key={item.id} className="border-t border-gray-100 align-top">
                  <td className="px-4 py-3 whitespace-nowrap">{new Date(item.createdAt).toLocaleString('pt-BR')}</td>
                  <td className="px-4 py-3">
                    <div>{item.originalName}</div>
                    {item.errorMessage && <div className="text-red-600 mt-1">{item.errorMessage}</div>}
                  </td>
                  <td className="px-4 py-3">{item.origin === 'email' ? 'E-mail' : 'Manual'}</td>
                  <td className="px-4 py-3">{item.actor}</td>
                  <td className="px-4 py-3">{item.statusLabel}</td>
                  <td className="px-4 py-3">{item.rowCount}</td>
                  {guaibim && <td className="px-4 py-3">{item.activeCount}</td>}
                  {guaibim && <td className="px-4 py-3">{item.cancelledCount}</td>}
                  {!guaibim && <td className="px-4 py-3">{MAXPAR_LABELS[item.maxparStatus] || '—'}</td>}
                  {canIssue && (
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => downloadReport(item, 'excel')}
                          disabled={downloadingKey === `${item.id}:excel`}
                          className="inline-flex items-center px-3 py-1 text-xs font-medium rounded text-primary-700 bg-primary-50 hover:bg-primary-100 disabled:opacity-60"
                        >
                          <Download className="h-3 w-3 mr-1" />
                          {downloadingKey === `${item.id}:excel` ? 'Gerando...' : 'Excel'}
                        </button>
                        <button
                          type="button"
                          onClick={() => downloadReport(item, 'pdf')}
                          disabled={downloadingKey === `${item.id}:pdf`}
                          className="inline-flex items-center px-3 py-1 text-xs font-medium rounded text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-60"
                        >
                          <Download className="h-3 w-3 mr-1" />
                          {downloadingKey === `${item.id}:pdf` ? 'Gerando...' : 'Fatura'}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
                </Fragment>
              ))}
              {!loading && items.length === 0 && (
                <tr>
                  <td className="px-4 py-3 text-gray-500" colSpan={columnCount}>Nenhum processamento encontrado.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

export default ProductProcessings;
